import { useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useAttachments } from './useAttachments';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

/**
 * One composer's attachments, wired to the upload endpoint.
 *
 * Wraps `useAttachments` (state + lifecycle) with the transport so no surface
 * has to restate either. Uploads go to `POST /api/email/attachments` as
 * multipart (field name `file`), and the server answers
 * `{s3_key, filename, content_type, size}`; the SEND payload carries that key,
 * never the bytes — which is why a 3 MB file does not
 * have to fit in a JSON body twice.
 *
 * The server enforces the size and type limits again on that endpoint and again
 * at send. The client-side check in `AttachmentBar` is a convenience so the user
 * learns immediately, exactly as `from_address` is a request rather than an
 * instruction.
 */
export function useAttachmentUpload() {
    const { getAuthHeader } = useAuth();

    const upload = useCallback(async (file, onProgress) => {
        const form = new FormData();
        form.append('file', file);
        const res = await axios.post(`${API}/email/attachments`, form, {
            headers: { ...getAuthHeader() },
            onUploadProgress: (event) => {
                if (!event.total) return;
                onProgress(Math.min(99, Math.round((event.loaded * 100) / event.total)));
            },
        });
        return res.data;
    }, [getAuthHeader]);

    const state = useAttachments(upload);

    /**
     * Add files, reporting any the client can already tell will be refused.
     * The toast names the file and the reason — #623 is explicit that a
     * failure discovered after send is the worst outcome.
     */
    const addAttachments = useCallback(async (files) => {
        const rejected = await state.add(files);
        (rejected || []).forEach(reason => toast.error(reason));
    }, [state]);

    return { ...state, addAttachments };
}

export default useAttachmentUpload;
